import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import offlineManager from '../../utils/offlineManager';

export default function MobileOrderReception() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showToast, showConfirm } = useUI();

    const [order, setOrder] = useState(null);
    const [items, setItems] = useState([]);
    const [deliveryDate, setDeliveryDate] = useState('');
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
    const [loading, setLoading] = useState(true);
    const [isCorrecting, setIsCorrecting] = useState(false);

    useEffect(() => {
        const fetchOrderDetails = async () => {
            if (!user?.companyId) return;
            try {
                // Fetch Order
                const resOrder = await fetch(`/api/purchase-orders/${id}`);
                if (!resOrder.ok) throw new Error('Commande introuvable');
                const orderData = await resOrder.json();

                if (orderData.status === 'Brouillon') {
                    showToast(`Réception impossible (Statut: Brouillon)`, 'warning');
                    navigate('/mobile');
                    return;
                }

                // Fetch Offline Receptions for this order
                const queuedReceptions = await offlineManager.getQueuedReceptions();
                const pendingForThisOrder = queuedReceptions.filter(r => r.orderId === orderData.id);

                setOrder(orderData);
                setItems((orderData.items || []).map(i => {
                    // Start with what the server says
                    let previouslyReceived = i.receivedQty || 0;

                    // Add ALL deltas pending in the offline queue to get the current local state
                    pendingForThisOrder.forEach(pr => {
                        const pendingItem = pr.items.find(pi => pi.id === i.id);
                        if (pendingItem && pendingItem.delta !== undefined) {
                            previouslyReceived += Number(pendingItem.delta);
                        }
                    });

                    return {
                        ...i,
                        previouslyReceived: previouslyReceived,
                        currentReceivedQty: previouslyReceived // Default value is the current cumulative state
                    };
                }));
                setDeliveryDate(orderData.deliveryDate ? orderData.deliveryDate.split('T')[0] : new Date().toISOString().split('T')[0]);

                // Fetch Warehouses
                const resWarehouses = await fetch(`/api/warehouses?companyId=${user.companyId}`);
                if (resWarehouses.ok) {
                    const data = await resWarehouses.json();
                    setWarehouses(data);
                    if (data.length > 0) {
                        const defaultWH = orderData.warehouseId ? orderData.warehouseId.toString() : data[0].id.toString();
                        setSelectedWarehouseId(defaultWH);
                    }
                }
            } catch (err) {
                console.error(err);
                showToast(err.message || 'Erreur lors du chargement', 'error');
                navigate('/mobile');
            } finally {
                setLoading(false);
            }
        };

        fetchOrderDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, user?.companyId]);

    const updateReceivedQty = (itemId, qty) => {
        setItems(items.map(i => {
            if (i.id !== itemId) return i;
            const finalQty = Math.max(0, Math.min(qty, i.quantity));
            return { ...i, currentReceivedQty: finalQty };
        }));
    };

    const handleReceiveAll = () => {
        setItems(items.map(i => ({ ...i, currentReceivedQty: i.quantity })));
    };

    const handleClearAll = () => {
        setItems(items.map(i => ({ ...i, currentReceivedQty: 0 })));
    };

    const totalOrdered = items.reduce((s, i) => s + i.quantity, 0);
    const totalCurrentReceived = items.reduce((s, i) => s + (i.currentReceivedQty || 0), 0);
    const totalPreviouslyReceived = items.reduce((s, i) => s + (i.previouslyReceived || 0), 0);

    // Derived Status for UI consistency (matches search list)
    let effectiveStatus = order?.status;
    if (totalCurrentReceived !== totalPreviouslyReceived) {
        if (totalCurrentReceived >= totalOrdered) {
            effectiveStatus = 'Clôturée';
        } else if (totalCurrentReceived > 0) {
            effectiveStatus = 'Partiellement Livrée';
        } else {
            effectiveStatus = order?.status === 'Livrée' ? 'Livrée' : 'Envoyée';
        }
    }

    const isEffectiveClosed = effectiveStatus === 'Clôturée';
    const isClosed = order?.status === 'Clôturée';
    const hasChanges = items.some(i => i.currentReceivedQty !== i.previouslyReceived);
    const allFullyReceived = items.every(i => i.currentReceivedQty >= i.quantity);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!hasChanges) {
            showToast("Aucune modification à enregistrer", "warning");
            return;
        }

        if (!selectedWarehouseId) {
            showToast("Veuillez sélectionner un entrepôt de réception", "error");
            return;
        }

        let confirmMsg = 'Confirmez-vous cette réception partielle ?';
        if (allFullyReceived) confirmMsg = 'Confirmez-vous la réception totale ? Cela clôturera la commande.';

        showConfirm({
            title: 'Valider la réception ?',
            message: confirmMsg,
            onConfirm: async () => {
                const payload = {
                    orderId: order.id,
                    reference: order.reference,
                    supplierName: order.supplier?.name,
                    items: items.filter(i => i.currentReceivedQty !== i.previouslyReceived).map(i => ({
                        id: i.id,
                        receivedQty: i.currentReceivedQty,
                        delta: i.currentReceivedQty - i.previouslyReceived, // The increment/decrement
                        productId: i.productId,
                        name: i.product?.name || i.catalogItem?.name
                    })),
                    deliveryDate,
                    warehouseId: selectedWarehouseId,
                    companyId: user.companyId,
                    userId: user.id
                };

                try {
                    if (!navigator.onLine) {
                        await offlineManager.queueReception(payload);
                        showToast(`Mouvement enregistré localement (${payload.items.length} articles)`, "success");
                        navigate('/mobile');
                        return;
                    }

                    const res = await fetch(`/api/purchase-orders/${order.id}/receive`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    });

                    if (res.ok) {
                        const data = await res.json();
                        const msg = data.status === 'Clôturée'
                            ? "Commande clôturée — stock mis à jour"
                            : "Réception enregistrée";
                        showToast(msg, "success");
                        navigate('/mobile');
                    } else {
                        // If online but server returned an error (4xx/5xx), don't queue!
                        const errorData = await res.json().catch(() => ({}));
                        showToast(errorData.error || `Erreur serveur (${res.status})`, "error");
                    }
                } catch (error) {
                    console.error("Submit error", error);
                    // Only queue if it's a real network error (offline)
                    if (!navigator.onLine) {
                        await offlineManager.queueReception(payload);
                        showToast("Mise en attente (Hors-ligne)", "info");
                        navigate('/mobile');
                    } else {
                        showToast("Erreur de connexion : " + error.message, "error");
                    }
                }
            }
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-10 h-[50vh]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!order) return null;

    return (
        <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 pb-[80px]">
            {/* Header */}
            <div className="bg-white dark:bg-slate-800 p-4 border-b border-slate-200 dark:border-slate-700 flex flex-col gap-2 shadow-sm sticky top-0 z-10">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500"
                    >
                        <span className="material-symbols-outlined text-2xl">arrow_back</span>
                    </button>
                    <h1 className="font-bold text-lg truncate flex-1 text-center pr-10">
                        {effectiveStatus === 'Clôturée' ? 'Détails ' : 'Réception '} {order.reference}
                    </h1>
                </div>
                <div className="flex justify-between items-center text-sm px-2">
                    <span className="text-slate-500 line-clamp-1 max-w-[60%]">{order.supplier?.name}</span>
                    <span className={`font-bold px-2 py-0.5 rounded text-xs ${effectiveStatus === 'Clôturée' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                        {effectiveStatus}
                    </span>
                </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4 flex-1 overflow-y-auto">
                {/* Banner for closed orders */}
                {effectiveStatus === 'Clôturée' && !isCorrecting && (
                    <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex flex-col gap-3 animate-in fade-in slide-in-from-top-4">
                        <div className="flex items-start gap-3">
                            <span className="material-symbols-outlined text-emerald-500">verified</span>
                            <div className="flex-1">
                                <p className="font-bold text-emerald-700 dark:text-emerald-400 text-sm">Commande clôturée</p>
                                <p className="text-xs text-emerald-600 dark:text-emerald-500/70 font-medium">Tous les articles ont été réceptionnés.</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsCorrecting(true)}
                            className="w-full py-2 px-4 bg-white dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-500 hover:text-white transition-all shadow-sm"
                        >
                            Corriger cette réception
                        </button>
                    </div>
                )}

                {/* Banner for correction mode */}
                {isCorrecting && (
                    <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl flex items-start gap-3 animate-in fade-in slide-in-from-top-4">
                        <span className="material-symbols-outlined text-amber-500">warning</span>
                        <div className="flex-1">
                            <p className="font-bold text-amber-700 dark:text-amber-400 text-sm italic">Mode Correction Activé</p>
                            <p className="text-xs text-amber-600 dark:text-amber-500/70 font-medium leading-relaxed">
                                Vous pouvez ajuster les quantités (ex: -1 pour retirer du stock). La commande sera ré-ouverte si nécessaire.
                            </p>
                        </div>
                    </div>
                )}
                {/* Progress */}
                <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                    <div className="flex justify-between items-end mb-2">
                        <span className="font-bold text-sm text-slate-700 dark:text-slate-300">Progression globale</span>
                        <span className="text-lg font-black text-primary">{totalCurrentReceived} / {totalOrdered}</span>
                    </div>
                    <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all duration-500 flex items-center justify-end pr-1 text-[8px] font-bold text-white ${allFullyReceived ? 'bg-green-500' : 'bg-primary'}`}
                            style={{ width: `${totalOrdered > 0 ? (totalCurrentReceived / totalOrdered) * 100 : 0}%` }}
                        >
                            {totalOrdered > 0 && totalCurrentReceived > 0 ? `${Math.round((totalCurrentReceived / totalOrdered) * 100)}%` : ''}
                        </div>
                    </div>
                </div>

                {/* Items */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center px-1">
                        <span className="text-sm font-bold text-slate-500 uppercase tracking-wide">Articles ({items.length})</span>
                        {(!isClosed || isCorrecting) && (
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleClearAll}
                                    className="bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 px-3 py-1.5 rounded-lg text-xs font-bold uppercase flex items-center gap-1 active:scale-95 transition-transform border border-amber-200 dark:border-amber-800"
                                >
                                    <span className="material-symbols-outlined text-sm">restart_alt</span>
                                    Tout vider
                                </button>
                                <button
                                    type="button"
                                    onClick={handleReceiveAll}
                                    className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 px-3 py-1.5 rounded-lg text-xs font-bold uppercase flex items-center gap-1 active:scale-95 transition-transform"
                                >
                                    <span className="material-symbols-outlined text-sm">done_all</span>
                                    Tout remplir
                                </button>
                            </div>
                        )}
                    </div>

                    {items.map(item => {
                        const fullyReceived = item.currentReceivedQty >= item.quantity;
                        const hadPreviousReception = item.previouslyReceived > 0;
                        const qtyTarget = item.quantity;

                        return (
                            <div key={item.id} className={`bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-sm border ${fullyReceived ? 'border-green-300 dark:border-green-800/50 relative overflow-hidden' : 'border-slate-200 dark:border-slate-700'}`}>
                                {fullyReceived && (
                                    <div className="absolute top-0 right-0 w-8 h-8 bg-green-500 rounded-bl-xl flex items-center justify-center pointer-events-none">
                                        <span className="material-symbols-outlined text-white text-sm">check</span>
                                    </div>
                                )}
                                <div className="mb-3 pr-6">
                                    <p className="font-bold text-base leading-tight dark:text-white">
                                        {item.product?.name || item.catalogItem?.name || 'Inconnu'}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-1 uppercase font-mono">
                                        {item.product?.sku || item.catalogItem?.sku || 'N/A'}
                                    </p>
                                </div>

                                <div className="grid grid-cols-[90px_1fr] gap-3">
                                    <div className="bg-slate-50 dark:bg-slate-900/50 p-2 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center min-h-[80px]">
                                        <span className="text-[10px] text-slate-400 font-bold uppercase mb-1">Cmdé</span>
                                        <span className="text-lg font-black text-slate-700 dark:text-slate-300">{qtyTarget}</span>
                                        {hadPreviousReception && (
                                            <span className="text-[9px] text-blue-500 font-bold mt-1">Reçu: {item.previouslyReceived}</span>
                                        )}
                                    </div>
                                    <div className="flex flex-col justify-end">
                                        <label className="text-[10px] text-slate-400 font-bold uppercase mb-1 text-center">Qté reçue</label>
                                        {(isClosed && !isCorrecting) ? (
                                            <div className="h-12 flex items-center justify-center bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-700">
                                                <span className="font-black text-xl text-emerald-600 dark:text-emerald-400 font-mono italic">
                                                    {item.currentReceivedQty}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center active:scale-95 transition-transform shrink-0"
                                                    onClick={() => updateReceivedQty(item.id, item.currentReceivedQty - 1)}
                                                >
                                                    <span className="material-symbols-outlined text-slate-600 dark:text-slate-300">remove</span>
                                                </button>
                                                <input
                                                    type="number"
                                                    inputMode="numeric"
                                                    min="0"
                                                    max={item.quantity}
                                                    value={item.currentReceivedQty === 0 ? '' : item.currentReceivedQty}
                                                    onChange={e => updateReceivedQty(item.id, parseInt(e.target.value) || 0)}
                                                    className={`flex-1 h-12 text-center font-black text-2xl rounded-xl outline-none border-2 focus:border-primary transition-colors hide-spin-button ${fullyReceived ? 'border-green-400 text-green-700 dark:border-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'}`}
                                                />
                                                <button
                                                    type="button"
                                                    className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center active:scale-95 transition-transform shrink-0"
                                                    onClick={() => updateReceivedQty(item.id, item.currentReceivedQty + 1)}
                                                >
                                                    <span className="material-symbols-outlined text-slate-600 dark:text-slate-300">add</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Validation Form */}
                <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 mt-6 space-y-4">
                    <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm">Informations de réception</h3>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Entrepôt de stockage</label>
                        <div className="relative">
                            <select
                                value={selectedWarehouseId}
                                onChange={(e) => setSelectedWarehouseId(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none"
                            >
                                {warehouses.map(w => (
                                    <option key={w.id} value={w.id}>{w.name}</option>
                                ))}
                            </select>
                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-primary">warehouse</span>
                            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">expand_more</span>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Date d'arrivée</label>
                        <div className="relative">
                            <input
                                type="date"
                                value={deliveryDate}
                                onChange={(e) => setDeliveryDate(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                            />
                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-primary">calendar_today</span>
                        </div>
                    </div>

                    {(!isClosed || isCorrecting) && (
                        <button
                            onClick={handleSubmit}
                            disabled={!hasChanges}
                            className="w-full mt-4 bg-primary text-white font-bold py-4 rounded-xl shadow-lg shadow-primary/30 active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {allFullyReceived ? (
                                <>
                                    <span className="material-symbols-outlined">inventory_2</span>
                                    {isCorrecting ? 'Enregistrer la correction' : `Clôturer (${totalCurrentReceived} reçus)`}
                                </>
                            ) : (
                                <>
                                    <span className="material-symbols-outlined">check</span>
                                    {isCorrecting ? 'Enregistrer la correction' : 'Réception partielle'}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
